import { useState, useCallback } from 'react'
import { PeriodValidationError } from '../types'
import { validateDateRange, validateWeeks, getYearFromDate, isValidDate } from '../utils/periodValidation'

interface UsePeriodValidationProps {
  initialPeriodFrom: Date | null
  initialPeriodTo: Date | null
  initialPeriodWeek: string[]
  initialPeriodYear: number | null
}

interface UsePeriodValidationReturn {
  periodFrom: Date | null
  periodTo: Date | null
  periodWeek: string[]
  periodYear: number | null
  periodValidationError: PeriodValidationError | null
  hasPeriodErrors: boolean
  setPeriodFrom: (date: Date | null) => void
  setPeriodTo: (date: Date | null) => void
  setPeriodWeek: (weeks: string[]) => void
  setPeriodYear: (year: number | null) => void
  resetPeriodValidation: () => void
}

const usePeriodValidation = ({
  initialPeriodFrom,
  initialPeriodTo,
  initialPeriodWeek,
  initialPeriodYear,
}: UsePeriodValidationProps): UsePeriodValidationReturn => {
  const [periodFrom, setPeriodFromState] = useState<Date | null>(initialPeriodFrom)
  const [periodTo, setPeriodToState] = useState<Date | null>(initialPeriodTo)
  const [periodWeek, setPeriodWeekState] = useState<string[]>(initialPeriodWeek)
  const [periodYear, setPeriodYearState] = useState<number | null>(initialPeriodYear)
  const [periodValidationError, setPeriodValidationError] = useState<PeriodValidationError | null>(null)

  // Validar fechas cuando cambian
  const validateDates = useCallback((from: Date | null, to: Date | null) => {
    const error = validateDateRange(from, to)
    setPeriodValidationError(error)
    return error === null
  }, [])

  // Validar semanas cuando cambian
  const validateWeeksState = useCallback((weeks: string[], year: number | null) => {
    if (year === null) {
      setPeriodValidationError(null)
      return true
    }
    const error = validateWeeks(weeks, year)
    setPeriodValidationError(error)
    return error === null
  }, [])

  const setPeriodFrom = useCallback((date: Date | null) => {
    const safeDate = isValidDate(date) ? date : null
    setPeriodFromState((prevFrom) => {
      // Si hay semanas seleccionadas, las limpiamos (mutua exclusión)
      setPeriodWeekState((prevWeeks) => {
        if (prevWeeks.length > 0) {
          validateWeeksState([], periodYear)
          return []
        }
        return prevWeeks
      })

      // Si se está seteando una fecha, limpiamos el año
      if (safeDate !== null) {
        setPeriodYearState(null)
      }

      // Si la nueva fecha tiene un año diferente al de 'to', limpiamos 'to'
      if (safeDate && periodTo) {
        const newYear = getYearFromDate(safeDate)
        const toYear = getYearFromDate(periodTo)
        if (newYear !== toYear) {
          setPeriodToState(null)
          validateDates(safeDate, null)
          return safeDate
        }
      }

      validateDates(safeDate, periodTo)
      return safeDate
    })
  }, [periodTo, periodYear, validateDates, validateWeeksState])

  const setPeriodTo = useCallback((date: Date | null) => {
    const safeDate = isValidDate(date) ? date : null
    setPeriodToState((prevTo) => {
      // Si hay semanas seleccionadas, las limpiamos (mutua exclusión)
      setPeriodWeekState((prevWeeks) => {
        if (prevWeeks.length > 0) {
          validateWeeksState([], periodYear)
          return []
        }
        return prevWeeks
      })

      // Si se está seteando una fecha, limpiamos el año
      if (safeDate !== null) {
        setPeriodYearState(null)
      }

      // Si la nueva fecha tiene un año diferente al de 'from', limpiamos 'from'
      if (safeDate && periodFrom) {
        const newYear = getYearFromDate(safeDate)
        const fromYear = getYearFromDate(periodFrom)
        if (newYear !== fromYear) {
          setPeriodFromState(null)
          validateDates(null, safeDate)
          return safeDate
        }
      }

      validateDates(periodFrom, safeDate)
      return safeDate
    })
  }, [periodFrom, periodYear, validateDates, validateWeeksState])

  const setPeriodWeek = useCallback((weeks: string[]) => {
    setPeriodWeekState((prevWeeks) => {
      // Si hay fechas seleccionadas, las limpiamos (mutua exclusión)
      if (periodFrom || periodTo) {
        setPeriodFromState(null)
        setPeriodToState(null)
        validateDates(null, null)
      }

      validateWeeksState(weeks, periodYear)
      return weeks
    })
  }, [periodFrom, periodTo, periodYear, validateDates, validateWeeksState])

  const setPeriodYear = useCallback((year: number | null) => {
    setPeriodYearState((prevYear) => {
      // Si hay fechas seleccionadas, las limpiamos (mutua exclusión fechas vs año)
      if (periodFrom || periodTo) {
        setPeriodFromState(null)
        setPeriodToState(null)
        validateDates(null, null)
      }

      // Si el año es null, limpiamos las semanas
      if (year === null) {
        setPeriodWeekState((prevWeeks) => {
          if (prevWeeks.length > 0) {
            validateWeeksState([], null)
            return []
          }
          return prevWeeks
        })
      } else {
        // Si cambia el año y hay semanas seleccionadas, limpiamos las semanas
        setPeriodWeekState((prevWeeks) => {
          if (prevWeeks.length > 0 && year !== prevYear) {
            validateWeeksState([], year)
            return []
          }
          // Si no cambió el año, revalidamos con el mismo año
          if (prevWeeks.length > 0) {
            validateWeeksState(prevWeeks, year)
          }
          return prevWeeks
        })
      }

      return year
    })
  }, [periodFrom, periodTo, validateDates, validateWeeksState])

  const resetPeriodValidation = useCallback(() => {
    setPeriodFromState(null)
    setPeriodToState(null)
    setPeriodWeekState([])
    setPeriodYearState(new Date().getFullYear())
    setPeriodValidationError(null)
  }, [])

  const hasPeriodErrors = periodValidationError !== null

  return {
    periodFrom,
    periodTo,
    periodWeek,
    periodYear,
    periodValidationError,
    hasPeriodErrors,
    setPeriodFrom,
    setPeriodTo,
    setPeriodWeek,
    setPeriodYear,
    resetPeriodValidation,
  }
}

export { usePeriodValidation }
